import { destroy, getWithConfig, postCustomize, put } from "../config/api";
import { getClient, getToken, getUid } from "./AuthService";

export type NoticeStatus = "draft" | "scheduled" | "live" | "ended";

export interface AdminNoticeSummary {
  id: number;
  title: string;
  startsAt: string;
  endsAt: string;
}

export interface AdminNotice extends AdminNoticeSummary {
  body: string;
  confirmationLabel: string;
  actionLabel: string | null;
  actionUrl: string | null;
  status: NoticeStatus;
  publishedAt: string | null;
  publishedBy: { id: number; name: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminNoticeForm {
  title: string;
  body: string;
  confirmationLabel: string;
  actionLabel: string;
  actionUrl: string;
  startsAt: string;
  endsAt: string;
}

export type AdminNoticeErrors = { [field: string]: string[] | undefined };

export interface AdminNoticeFailure {
  errors: AdminNoticeErrors;
  conflict?: AdminNoticeSummary;
}

const authHeaders = () => ({
  "Access-Token": getToken(),
  client: getClient(),
  Uid: getUid(),
});

const path = (id: number, suffix = "") => `/admin/avisos/${id}${suffix}`;

export default class AdminNoticesService {
  public static async list() {
    return getWithConfig<AdminNotice[]>("/admin/avisos", { headers: authHeaders() })
      .then((response) => response.data);
  }

  public static async create(form: AdminNoticeForm) {
    return postCustomize<AdminNotice>("/admin/avisos", form, { headers: authHeaders() })
      .then((response) => response.data);
  }

  public static async update(id: number, form: AdminNoticeForm) {
    return put<AdminNotice>(path(id), form, { headers: authHeaders() })
      .then((response) => response.data);
  }

  public static async remove(id: number) {
    return destroy<void>(path(id), { headers: authHeaders() })
      .then(() => undefined);
  }

  public static async publish(id: number) {
    return postCustomize<AdminNotice>(path(id, "/publicacao"), {}, { headers: authHeaders() })
      .then((response) => response.data);
  }

  public static async unpublish(id: number) {
    return destroy<AdminNotice>(path(id, "/publicacao"), { headers: authHeaders() })
      .then((response) => response.data);
  }

  public static async duplicate(id: number) {
    return postCustomize<AdminNotice>(path(id, "/duplicacao"), {}, { headers: authHeaders() })
      .then((response) => response.data);
  }
}

export function noticeFailure(error: unknown): AdminNoticeFailure | null {
  if (typeof error !== "object" || error === null) {
    return null;
  }

  const { errors, conflict } = error as { errors?: unknown; conflict?: AdminNoticeSummary };
  if (typeof errors !== "object" || errors === null || Array.isArray(errors)) {
    return null;
  }

  return { errors: errors as AdminNoticeErrors, conflict };
}
